'use client'

import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { toast } from '@/components/ui/toast'
import { subscribeDemoState } from '@/lib/api-client'

/** Many writes can fail together during a reset; one toast per window is enough. */
const MAINTENANCE_TOAST_GAP_MS = 15_000

/**
 * Shell-wide view of the Master Reset signals the API client sees (plan docs/data-migration/03,
 * "What users see during a reset"): a toast when a write is refused for maintenance, and a
 * reload prompt once the demo state generation has moved on from the one this tab loaded with.
 */
export function DemoStateWatcher() {
  const [changed, setChanged] = useState(false)
  const lastMaintenanceToast = useRef(0)

  useEffect(
    () =>
      subscribeDemoState((event) => {
        if (event.type === 'generation-changed') {
          setChanged(true)
          return
        }
        const now = Date.now()
        if (now - lastMaintenanceToast.current < MAINTENANCE_TOAST_GAP_MS) return
        lastMaintenanceToast.current = now
        toast.warning('Demo data is being reset', {
          description:
            event.retryAfterSeconds !== null
              ? `${event.message} (about ${event.retryAfterSeconds} s)`
              : event.message,
        })
      }),
    []
  )

  if (!changed) return null

  return (
    <div
      role='status'
      aria-live='polite'
      className='fixed inset-x-4 bottom-4 z-toast mx-auto flex max-w-xl flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-float sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2'
    >
      <Icons.refresh className='h-5 w-5 shrink-0 text-brand-purple' strokeWidth={1.5} aria-hidden='true' />
      <p className='min-w-0 flex-1 text-sm text-foreground'>
        The demo data was reset. Reload to see the current data.
      </p>
      <div className='flex gap-2'>
        <Button size='sm' variant='outline' onClick={() => setChanged(false)}>
          Later
        </Button>
        <Button size='sm' onClick={() => window.location.reload()}>
          Reload
        </Button>
      </div>
    </div>
  )
}
